import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import Shell from "../components/Shell";
import IptvPlayer from "../components/IptvPlayer";
import { loadPrefs, savePrefs } from "../prefs";

const PAGE_SIZE = 50;
const SEARCH_MIN = 2;
const PINNED_CARACOL_ID = "49652a0738d71265";

const REGION_FLAGS = {
  CO: "🇨🇴",
  US: "🇺🇸",
  MX: "🇲🇽",
  AR: "🇦🇷",
  CL: "🇨🇱",
  PE: "🇵🇪",
  VE: "🇻🇪",
  EC: "🇪🇨",
  BR: "🇧🇷",
  ES: "🇪🇸",
  INT: "🌐",
};

const REGION_ORDER = ["CO", "US", "MX", "AR", "CL", "ES", "PE", "VE", "EC", "BR", "INT"];

const KIND_ICONS = {
  deportes: "⚽",
  noticias: "📰",
  peliculas: "🎬",
  series: "📺",
  entretenimiento: "🎭",
  infantil: "🧒",
  musica: "🎵",
  documentales: "🌍",
  educacion: "📚",
  religion: "✝️",
};

function displayGroup(name) {
  const n = String(name || "").trim();
  if (!n || n.toLowerCase() === "undefined") return "General";
  if (/^colombia$/i.test(n)) return "General";
  return n.replace(/\s*[-–|]\s*colombia\s*$/i, "").trim() || "General";
}

function orderFeatured(list) {
  const ok = (list || []).filter((ch) => ch.playable !== false);
  const pinned = ok.find((ch) => ch.id === PINNED_CARACOL_ID);
  if (!pinned) return ok;
  return [pinned, ...ok.filter((ch) => ch.id !== PINNED_CARACOL_ID)];
}

function sortRegions(regions) {
  const byCode = new Map((regions || []).map((r) => [r.code, r]));
  const ordered = [];
  for (const code of REGION_ORDER) {
    if (byCode.has(code)) ordered.push(byCode.get(code));
  }
  for (const r of regions || []) {
    if (!REGION_ORDER.includes(r.code)) ordered.push(r);
  }
  return ordered;
}

export default function IptvPage() {
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const [browse, setBrowse] = useState(null);
  const [channels, setChannels] = useState([]);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loadingBrowse, setLoadingBrowse] = useState(true);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [error, setError] = useState("");
  const [filterView, setFilterView] = useState(() => (params.get("region") ? "categories" : "countries"));

  const kind = params.get("kind") || "";
  const urlQ = (params.get("q") || "").trim();
  const region = params.get("region") || "";
  const activeId = params.get("ch") || "";
  const searchMode = urlQ.length >= SEARCH_MIN;
  const filtersRestoredRef = useRef(false);

  useEffect(() => {
    if (filtersRestoredRef.current) return;
    filtersRestoredRef.current = true;
    if (searchMode) return;
    if (params.get("region") || params.get("kind")) return;
    const prefs = loadPrefs();
    if (!prefs.iptvRegion && !prefs.iptvKind) return;
    const p = new URLSearchParams(params);
    if (prefs.iptvRegion) p.set("region", prefs.iptvRegion);
    if (prefs.iptvKind) p.set("kind", prefs.iptvKind);
    setParams(p, { replace: true });
    if (prefs.iptvRegion) setFilterView("categories");
  }, [searchMode, params, setParams]);

  const loadBrowse = useCallback(async () => {
    if (!token) return;
    setLoadingBrowse(true);
    setError("");
    try {
      const qs = new URLSearchParams();
      if (region) qs.set("region", region);
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      const data = await api(`/api/iptv/browse${suffix}`, { token });
      setBrowse(data);
    } catch (err) {
      setError(err.message || "No se pudo cargar la TV en vivo");
      setBrowse(null);
    } finally {
      setLoadingBrowse(false);
    }
  }, [token, region]);

  const loadRegions = useCallback(async () => {
    if (!token) return;
    try {
      const data = await api("/api/iptv/browse", { token });
      setBrowse((prev) => ({
        ...(prev || {}),
        regions: data.regions || [],
        playable_channels: data.playable_channels ?? prev?.playable_channels,
        total: data.total ?? prev?.total,
      }));
    } catch {
      /* ignore */
    }
  }, [token]);

  useEffect(() => {
    loadBrowse();
  }, [loadBrowse]);

  useEffect(() => {
    if (searchMode) return;
    loadRegions();
  }, [searchMode, loadRegions]);

  const fetchChannels = useCallback(
    async ({ append = false, nextOffset = 0 } = {}) => {
      if (!token) return;
      if (append) loadingMoreRef.current = true;
      setLoadingChannels(true);
      if (!append) setError("");
      try {
        const qs = new URLSearchParams({
          limit: String(PAGE_SIZE),
          offset: String(nextOffset),
        });
        if (region && !searchMode) qs.set("region", region);
        if (searchMode) qs.set("q", urlQ);
        else if (kind) qs.set("kind", kind);

        const data = await api(`/api/iptv/channels?${qs.toString()}`, { token });
        const list = data.channels || [];
        setChannels((prev) => (append ? [...prev, ...list] : list));
        setTotalFiltered(data.total_filtered ?? list.length);
        setHasMore(Boolean(data.has_more));
        setOffset(nextOffset + list.length);
      } catch (err) {
        setError(err.message || "No se pudieron cargar los canales");
        if (!append) setChannels([]);
      } finally {
        setLoadingChannels(false);
        loadingMoreRef.current = false;
      }
    },
    [token, searchMode, urlQ, kind, region]
  );

  useEffect(() => {
    setOffset(0);
    fetchChannels({ append: false, nextOffset: 0 });
  }, [fetchChannels]);

  useEffect(() => {
    if (!channels.length) return;
    if (activeId && channels.some((c) => c.id === activeId)) return;
    const pick = channels.find((c) => c.playable !== false) || channels[0];
    if (!pick) return;
    const p = new URLSearchParams(params);
    p.set("ch", pick.id);
    setParams(p, { replace: true });
  }, [channels, activeId, params, setParams]);

  useEffect(() => {
    const root = listRef.current;
    const target = sentinelRef.current;
    if (!root || !target || !hasMore) return undefined;

    const obs = new IntersectionObserver(
      (entries) => {
        if (
          !entries[0]?.isIntersecting ||
          loadingChannels ||
          loadingMoreRef.current ||
          !hasMore
        ) {
          return;
        }
        fetchChannels({ append: true, nextOffset: offset });
      },
      { root, rootMargin: "240px", threshold: 0 }
    );

    obs.observe(target);
    return () => obs.disconnect();
  }, [hasMore, loadingChannels, offset, fetchChannels]);

  const featured = orderFeatured(browse?.featured);
  const regions = sortRegions(browse?.regions || []);
  const kinds = browse?.kinds || [];

  const countryTabs = useMemo(() => {
    if (searchMode) return [];
    const total = browse?.playable_channels || browse?.total || 0;
    const tabs = [{ code: "", label: "Todos", flag: "🌎", count: total }];
    for (const r of regions) {
      tabs.push({
        code: r.code,
        label: r.label,
        flag: REGION_FLAGS[r.code] || "📡",
        count: r.count,
      });
    }
    return tabs;
  }, [regions, browse, searchMode]);

  const kindTabs = useMemo(() => {
    if (searchMode) return [];
    return [{ id: "", label: "Todos", count: null, icon: "✨" }, ...kinds.map((k) => ({
      ...k,
      icon: KIND_ICONS[k.id] || "📺",
    }))];
  }, [kinds, searchMode]);

  const activeCountry = countryTabs.find((c) => c.code === region) || null;

  const activeChannel = useMemo(
    () => channels.find((c) => c.id === activeId) || featured[0] || null,
    [channels, activeId, featured]
  );

  function pickCountry(code) {
    const p = new URLSearchParams(params);
    if (code) p.set("region", code);
    else p.delete("region");
    p.delete("kind");
    p.delete("q");
    p.delete("ch");
    savePrefs({ iptvRegion: code || "", iptvKind: "" });
    setParams(p, { replace: true });
    if (code) setFilterView("categories");
  }

  function setRegion(code) {
    pickCountry(code);
  }

  function setKind(id) {
    const p = new URLSearchParams(params);
    if (id) p.set("kind", id);
    else p.delete("kind");
    p.delete("q");
    p.delete("ch");
    savePrefs({ iptvKind: id || "" });
    setParams(p, { replace: true });
  }

  function selectChannel(id) {
    const p = new URLSearchParams(params);
    p.set("ch", id);
    setParams(p, { replace: true });
  }

  const listTitle = searchMode
    ? `Resultados para “${urlQ}”`
    : kind
      ? kindTabs.find((k) => k.id === kind)?.label || kind
      : region
        ? countryTabs.find((c) => c.code === region)?.label || "Canales"
        : "Canales";

  function handleChannelFailed(failedId) {
    setChannels((prev) => prev.filter((c) => c.id !== failedId));
    setTotalFiltered((prev) => Math.max(0, prev - 1));
    if (activeId !== failedId) return;
    const p = new URLSearchParams(params);
    p.delete("ch");
    setParams(p, { replace: true });
  }

  return (
    <Shell wide iptvLayout>
      <div className="dtv-app">
        {error ? <p className="error dtv-error">{error}</p> : null}

        <div className={`dtv-layout${searchMode ? "" : " dtv-layout-filters"}`}>
          {!searchMode ? (
            <aside className="iptv-filter-col" aria-label="Filtros">
              {filterView === "countries" ? (
                <>
                  <div className="iptv-filter-head">
                    <h2>País</h2>
                    <span className="iptv-filter-hint">Elige uno</span>
                  </div>
                  <nav className="iptv-filter-scroll">
                    <ul className="iptv-filter-list">
                      {countryTabs.map((tab) => (
                        <li key={tab.code || "all"}>
                          <button
                            type="button"
                            className={`iptv-filter-item${region === tab.code ? " is-active" : ""}`}
                            onClick={() => pickCountry(tab.code)}
                          >
                            <span className="iptv-filter-icon" aria-hidden="true">
                              {tab.flag}
                            </span>
                            <span className="iptv-filter-body">
                              <strong>{tab.label}</strong>
                              {tab.count != null ? (
                                <span>{tab.count.toLocaleString("es")} canales</span>
                              ) : null}
                            </span>
                            {tab.code ? (
                              <span className="iptv-filter-chevron" aria-hidden="true">
                                ›
                              </span>
                            ) : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </>
              ) : (
                <>
                  <div className="iptv-filter-head">
                    <button
                      type="button"
                      className="iptv-filter-back"
                      onClick={() => setFilterView("countries")}
                      aria-label="Volver a países"
                    >
                      ←
                    </button>
                    <div className="iptv-filter-head-main">
                      <h2>{activeCountry?.label || "Categorías"}</h2>
                      <span className="iptv-filter-hint">Categoría</span>
                    </div>
                  </div>
                  <nav className="iptv-filter-scroll">
                    <ul className="iptv-filter-list">
                      {kindTabs.map((tab) => (
                        <li key={tab.id || "all-k"}>
                          <button
                            type="button"
                            className={`iptv-filter-item${kind === tab.id ? " is-active" : ""}`}
                            onClick={() => setKind(tab.id)}
                          >
                            <span className="iptv-filter-icon" aria-hidden="true">
                              {tab.icon}
                            </span>
                            <span className="iptv-filter-body">
                              <strong>{tab.label}</strong>
                              {tab.count != null ? <span>{tab.count} canales</span> : null}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </>
              )}
            </aside>
          ) : null}

          <section className="dtv-guide dtv-guide-compact" aria-label="Lista de canales">
            <div className="dtv-guide-head">
              <div className="dtv-guide-head-main">
                <h2>{listTitle}</h2>
                {!searchMode && browse?.playable_channels ? (
                  <p className="dtv-guide-meta">
                    {(browse.playable_channels).toLocaleString("es")} con señal verificada
                  </p>
                ) : null}
              </div>
              <div className="dtv-guide-head-actions">
                {channels.length ? (
                  <span className="dtv-guide-count">
                    {channels.length}
                    {totalFiltered > channels.length ? ` / ${totalFiltered}` : ""}
                  </span>
                ) : null}
                {searchMode ? (
                  <button
                    type="button"
                    className="dtv-clear-search"
                    onClick={() => {
                      const p = new URLSearchParams(params);
                      p.delete("q");
                      setParams(p, { replace: true });
                    }}
                  >
                    Limpiar búsqueda
                  </button>
                ) : null}
              </div>
            </div>

            <div className="dtv-list-scroll" ref={listRef}>
              {loadingChannels && !channels.length ? (
                <ul className="dtv-channel-list">
                  {Array.from({ length: 14 }).map((_, i) => (
                    <li key={i} className="dtv-channel-row dtv-channel-row-skeleton" />
                  ))}
                </ul>
              ) : null}

              {channels.length ? (
                <ul className="dtv-channel-list">
                  {channels.map((ch, i) => (
                    <ChannelRow
                      key={ch.id}
                      channel={ch}
                      number={i + 1}
                      active={activeId === ch.id}
                      onSelect={() => selectChannel(ch.id)}
                    />
                  ))}
                  {hasMore ? (
                    <li ref={sentinelRef} className="dtv-list-sentinel" aria-hidden="true">
                      {loadingChannels ? "Cargando…" : ""}
                    </li>
                  ) : null}
                </ul>
              ) : null}

              {!loadingChannels && !channels.length ? (
                <div className="dtv-empty">
                  <p>
                    {searchMode
                      ? `No hay canales que coincidan con “${urlQ}”.`
                      : "No hay canales con señal en esta selección."}
                  </p>
                </div>
              ) : null}
            </div>
          </section>

          <aside className="dtv-player-col" aria-label="Reproductor">
            {activeChannel && token ? (
              <IptvPlayer
                key={activeChannel.id}
                channelId={activeChannel.id}
                token={token}
                channelName={activeChannel.name}
                channelGroup={displayGroup(activeChannel.group)}
                onChannelFailed={handleChannelFailed}
              />
            ) : (
              <div className="dtv-player-panel dtv-player-empty">
                <p>{loadingBrowse ? "Cargando…" : "Selecciona un canal"}</p>
              </div>
            )}
          </aside>
        </div>
      </div>
    </Shell>
  );
}

function ChannelRow({ channel, number, active, onSelect }) {
  if (channel.playable === false) return null;

  return (
    <li>
      <button
        type="button"
        className={`dtv-channel-row dtv-channel-row-compact${active ? " is-active" : ""}`}
        onClick={onSelect}
      >
        <span className="dtv-ch-logo">
          {channel.logo ? (
            <img src={channel.logo} alt="" loading="lazy" decoding="async" />
          ) : (
            <span>TV</span>
          )}
        </span>
        <span className="dtv-ch-body">
          <strong>{channel.name}</strong>
        </span>
        {active ? <span className="dtv-ch-now">▶</span> : null}
      </button>
    </li>
  );
}
